import { useState } from "react";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Alert,
  Box,
  Button,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Trans, useTranslation } from "react-i18next";
import { EmployeeOutstandingLoan } from "src/types/salary";

export interface EventDeductionLine {
  purchase_event_id: number | null;
  amount: number;
}

interface LoanCardProps {
  loans: EmployeeOutstandingLoan[];
  eventDeductions: EventDeductionLine[];
  onChange: (deductions: EventDeductionLine[]) => void;
}

const LoanCard = ({ loans, eventDeductions, onChange }: LoanCardProps) => {
  const { t } = useTranslation("salary");
  const { t: generalT } = useTranslation("general");
  const [dismissed, setDismissed] = useState(false);

  const totalLoanBalance = loans.reduce(
    (sum, l) => sum + l.remaining_balance,
    0,
  );

  if (loans.length === 0 || totalLoanBalance <= 0) return null;

  const hasAccepted = eventDeductions.length > 0;

  if (!hasAccepted && !dismissed) {
    return (
      <Alert
        severity="info"
        icon={<InfoOutlinedIcon fontSize="small" />}
        sx={{
          borderRadius: 1,
          "& .MuiAlert-message": { width: "100%" },
        }}
      >
        <Stack spacing={1}>
          <Typography fontSize={14}>
            <Trans
              i18nKey="salary:employeeLoanNotice"
              values={{ amount: totalLoanBalance.toFixed(2) }}
              components={{
                1: (
                  <Typography
                    component="span"
                    fontSize={14}
                    fontWeight={700}
                    color="#E5942B"
                  />
                ),
              }}
            />
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="contained"
              sx={{
                bgcolor: "#E5942B",
                "&:hover": { bgcolor: "#D07E1E" },
                textTransform: "none",
                minWidth: 60,
                fontSize: 13,
              }}
              onClick={() => {
                onChange([{ purchase_event_id: null, amount: 0 }]);
              }}
            >
              {generalT("yes")}
            </Button>
            <Button
              size="small"
              variant="outlined"
              sx={{
                borderColor: "#D0D5DD",
                color: "#344054",
                textTransform: "none",
                minWidth: 60,
                fontSize: 13,
              }}
              onClick={() => setDismissed(true)}
            >
              {generalT("no")}
            </Button>
          </Stack>
        </Stack>
      </Alert>
    );
  }

  if (dismissed) return null;

  const usedEventIds = new Set(
    eventDeductions
      .map((d) => d.purchase_event_id)
      .filter((id): id is number => id !== null),
  );

  const addLine = () => {
    onChange([...eventDeductions, { purchase_event_id: null, amount: 0 }]);
  };

  const removeLine = (index: number) => {
    const next = eventDeductions.filter((_, i) => i !== index);
    onChange(next.length > 0 ? next : []);
    if (next.length === 0) setDismissed(false);
  };

  const updateLine = (
    index: number,
    field: keyof EventDeductionLine,
    value: number | null,
  ) => {
    const next = [...eventDeductions];
    if (field === "purchase_event_id") {
      const loan = loans.find((l) => l.id === value);
      next[index] = {
        purchase_event_id: value,
        amount: loan?.remaining_balance ?? 0,
      };
    } else {
      next[index] = { ...next[index], [field]: value as number };
    }
    onChange(next);
  };

  return (
    <Box
      sx={{
        border: "1px solid #F9DBAF",
        borderRadius: 1,
        p: 2,
        bgcolor: "#FFFAF5",
      }}
    >
      <Stack spacing={2}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography
            fontSize={14}
            fontWeight={700}
            color="#E5942B"
            textTransform="uppercase"
          >
            {t("eventDeduction")}
          </Typography>
          {eventDeductions.length === 1 && (
            <IconButton
              size="small"
              onClick={() => removeLine(0)}
              sx={{ color: "tertiary.main" }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>

        {eventDeductions.map((line, index) => {
          const selectedLoan = loans.find(
            (l) => l.id === line.purchase_event_id,
          );
          const availableLoans = loans.filter(
            (l) => !usedEventIds.has(l.id) || l.id === line.purchase_event_id,
          );

          return (
            <Stack key={index} spacing={1.5}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography fontSize={13} color="#344054">
                  {t("selectItem")}
                </Typography>
                {eventDeductions.length > 1 && (
                  <IconButton
                    size="small"
                    onClick={() => removeLine(index)}
                    sx={{ color: "tertiary.main" }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                )}
              </Stack>

              <TextField
                size="small"
                select
                fullWidth
                value={line.purchase_event_id ?? ""}
                onChange={(e) => {
                  const val = e.target.value;
                  updateLine(
                    index,
                    "purchase_event_id",
                    val === "" ? null : Number(val),
                  );
                }}
              >
                <MenuItem value="">{t("selectItem")}</MenuItem>
                {availableLoans.map((loan) => (
                  <MenuItem key={loan.id} value={loan.id}>
                    {t("employeeLoanLabel", {
                      reference: loan.reference,
                      amount: loan.remaining_balance.toFixed(2),
                    })}
                  </MenuItem>
                ))}
              </TextField>

              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                gap={2}
              >
                <Box flex={1}>
                  <Typography fontSize={13} color="#344054" mb={0.5}>
                    {t("amountToDeduct")}
                  </Typography>
                  <TextField
                    size="small"
                    type="number"
                    fullWidth
                    value={line.amount || ""}
                    onChange={(e) => {
                      const val = Number(e.target.value) || 0;
                      const max = selectedLoan?.remaining_balance ?? 0;
                      updateLine(
                        index,
                        "amount",
                        Math.min(Math.max(0, val), max),
                      );
                    }}
                    inputProps={{
                      min: 0,
                      max: selectedLoan?.remaining_balance ?? 0,
                    }}
                    disabled={!line.purchase_event_id}
                  />
                </Box>
                <Box textAlign="right" minWidth={80} pt={2.5}>
                  <Typography fontSize={11} color="text.secondary" fontWeight={600}>
                    {t("balance")}
                  </Typography>
                  <Typography fontSize={14} color="#E5942B" fontWeight={700}>
                    {(selectedLoan?.remaining_balance ?? 0).toFixed(2)} SAR
                  </Typography>
                </Box>
              </Stack>
            </Stack>
          );
        })}

        {eventDeductions.length < loans.length && (
          <Button
            variant="text"
            size="small"
            onClick={addLine}
            sx={{
              alignSelf: "flex-start",
              textTransform: "none",
              color: "#E5942B",
            }}
          >
            + {t("addEventDeduction")}
          </Button>
        )}
      </Stack>
    </Box>
  );
};

export default LoanCard;
